/**
 * Energy Bar Component
 * Displays energy level as a horizontal progress bar with color gradient
 */

import { getEnergyLabel } from '../../lib/audio-features';

export interface EnergyBarProps {
  energy: number; // 0.0 - 1.0
  width?: string;
  showLabel?: boolean;
  className?: string;
}

/**
 * Get detailed energy color (more granular than audio-features util)
 */
function getDetailedEnergyColor(energy: number): string {
  if (energy >= 0.8) return 'bg-red-500';
  if (energy >= 0.6) return 'bg-orange-500';
  if (energy >= 0.4) return 'bg-yellow-500';
  if (energy >= 0.2) return 'bg-green-500';
  return 'bg-blue-500';
}

/**
 * Get energy description for tooltip
 */
function getEnergyDescription(energy: number): string {
  if (energy >= 0.8) return 'Very High Energy - Intense, fast, loud';
  if (energy >= 0.6) return 'High Energy - Upbeat and energetic';
  if (energy >= 0.4) return 'Medium Energy - Moderate tempo and intensity';
  if (energy >= 0.2) return 'Low Energy - Calm and relaxed';
  return 'Very Low Energy - Mellow, quiet, slow';
}

export function EnergyBar({
  energy,
  width = 'w-full',
  showLabel = false,
  className = '',
}: EnergyBarProps) {
  // Clamp energy between 0 and 1
  const clampedEnergy = Math.max(0, Math.min(1, energy));
  const percentage = Math.round(clampedEnergy * 100);
  const color = getDetailedEnergyColor(clampedEnergy);
  const description = getEnergyDescription(clampedEnergy);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Progress Bar */}
      <div
        className={`
          relative h-2 bg-zinc-800 rounded-full overflow-hidden
          ${width}
        `}
        title={description}
      >
        {/* Fill */}
        <div
          className={`
            absolute inset-y-0 left-0
            ${color}
            rounded-full
            transition-all duration-300
          `}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Optional Label */}
      {showLabel && (
        <span className="text-xs font-medium text-zinc-400 min-w-[3rem] text-right">
          {percentage}%
        </span>
      )}
    </div>
  );
}

/**
 * Compact variant with label inside the bar
 */
export interface EnergyBarCompactProps extends EnergyBarProps {
  height?: string;
}

export function EnergyBarCompact({
  energy,
  width = 'w-full',
  height = 'h-6',
  className = '',
}: EnergyBarCompactProps) {
  const clampedEnergy = Math.max(0, Math.min(1, energy));
  const percentage = Math.round(clampedEnergy * 100);
  const label = getEnergyLabel(clampedEnergy);
  const color = getDetailedEnergyColor(clampedEnergy);
  const description = getEnergyDescription(clampedEnergy);

  return (
    <div
      className={`
        relative ${width} ${height}
        bg-zinc-800 rounded-md overflow-hidden
        ${className}
      `}
      title={description}
    >
      {/* Fill */}
      <div
        className={`
          absolute inset-y-0 left-0
          ${color}
          transition-all duration-300
        `}
        style={{ width: `${percentage}%` }}
      />

      {/* Label */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-semibold text-white drop-shadow-md z-10">
          {label} ({percentage}%)
        </span>
      </div>
    </div>
  );
}
